/**
 * composedSave — plate proof 2b, design X (ruled by ORCH-15, 2026-10-09): "correct a value and prove it" is TWO existing steps in
 * sequence, never one new writer. Step 1: the existing save (updateGenerator for AppSheet-backed columns, fieldService_updateUnitRecord
 * for OfficeHub-owned ones, or an approval). Step 2: for every value that LANDED and carries the person's tick, the 2a confirm
 * (fieldService_plateProofConfirm). This module is the pure planning between the two: which values get a confirm request, and the
 * operator-truth result afterwards.
 *
 * THE GUARANTEES (each has a test that goes red without it):
 *  - a proof is requested ONLY for a value that landed (applied | queued | unchanged) AND was ticked "the plate backs this";
 *  - a failed confirm leaves "saved, not yet proven" and a retry of ONLY the confirm: it never over-claims proof;
 *  - the result keeps three facts per value (saved?, proven?, plain words) and is "all-done" only when every value is saved and
 *    every requested proof is confirmed; a missing confirm result counts as a failed confirm, never as done;
 *  - a stray confirm result for a value that was not requested cannot mark anything proven.
 * Pure: no I/O, no mutation of its input.
 */

/** What the existing save said about one value. `queued` = saved here, delivery to AppSheet still pending. */
export type SaveOutcome = 'applied' | 'queued' | 'unchanged' | 'refused' | 'failed';

export interface FieldSave {
  /** Canonical attribute key (component:<type>:<field>). */
  attrKey: string;
  outcome: SaveOutcome;
  /** The person's "the plate backs this" tick. A reader suggestion alone never sets it. */
  wantsProof: boolean;
  /** Plain-words reason from the save when it did not land. */
  reason?: string;
}

export type ValueFact = 'saved' | 'pending-delivery' | 'unchanged' | 'not-saved';
export type ProofFact = 'to-confirm' | 'not-requested' | 'blocked' | 'confirmed' | 'refused' | 'failed';

export interface ComposedRow {
  attrKey: string;
  value: ValueFact;
  proof: ProofFact;
  message: string;
}

export interface ComposedPlan {
  /** Attribute keys to send to the confirm step (landed AND ticked), in the order given. */
  confirmRequests: string[];
  rows: ComposedRow[];
}

export interface ConfirmResult {
  attrKey: string;
  outcome: 'confirmed' | 'already-confirmed' | 'refused' | 'error';
  reason?: string;
}

export type Overall = 'all-done' | 'partly-done' | 'nothing-saved';

export interface ComposedResult {
  overall: Overall;
  rows: ComposedRow[];
  /** Values whose confirm failed in transit: repeating ONLY the confirm for these is safe (the confirm is idempotent). */
  retryConfirmOnly: string[];
}

const LANDED: ReadonlySet<SaveOutcome> = new Set(['applied', 'queued', 'unchanged']);

function valueFact(outcome: SaveOutcome): ValueFact {
  if (outcome === 'applied') return 'saved';
  if (outcome === 'queued') return 'pending-delivery';
  return outcome === 'unchanged' ? 'unchanged' : 'not-saved';
}

function messageFor(save: FieldSave, proof: ProofFact): string {
  if (!LANDED.has(save.outcome)) return save.reason ? `Not saved: ${save.reason}` : 'Not saved.';
  const base = save.outcome === 'queued' ? 'Saved, pending delivery to AppSheet.' : save.outcome === 'unchanged' ? 'Already up to date.' : 'Saved.';
  return proof === 'not-requested' ? base : `${base} Plate confirmation next.`;
}

export function planComposedSave(saves: readonly FieldSave[]): ComposedPlan {
  const rows = saves.map((s): ComposedRow => {
    const landed = LANDED.has(s.outcome);
    const proof: ProofFact = !landed ? 'blocked' : s.wantsProof ? 'to-confirm' : 'not-requested';
    return { attrKey: s.attrKey, value: valueFact(s.outcome), proof, message: messageFor(s, proof) };
  });
  return { confirmRequests: rows.filter((r) => r.proof === 'to-confirm').map((r) => r.attrKey), rows };
}

function settle(row: ComposedRow, result: ConfirmResult | undefined): ComposedRow {
  if (row.proof !== 'to-confirm') return row;
  const pending = row.value === 'pending-delivery' ? ' Delivery to AppSheet is still pending.' : '';
  if (!result) return { ...row, proof: 'failed', message: `Saved, not yet proven: the plate confirmation did not complete. Try the confirmation again.${pending}` };
  if (result.outcome === 'confirmed' || result.outcome === 'already-confirmed') {
    return { ...row, proof: 'confirmed', message: `Saved and confirmed against the plate.${pending}` };
  }
  if (result.outcome === 'refused') return { ...row, proof: 'refused', message: `Saved, not proven: ${result.reason ?? 'the confirmation was refused'}` };
  return { ...row, proof: 'failed', message: `Saved, not yet proven: ${result.reason ?? 'the confirmation could not be sent'}. Try the confirmation again.${pending}` };
}

export function finishComposedSave(plan: ComposedPlan, confirms: readonly ConfirmResult[]): ComposedResult {
  // A result for a value that was not requested is ignored by settle() (only a 'to-confirm' row can become proven).
  // FIRST answer for a key wins (same rule as the client's toConfirmResults): a later 'confirmed' must not override an earlier 'refused'.
  // Callers must not request two spellings of one key.
  const byKey = new Map<string, ConfirmResult>();
  for (const c of confirms) if (!byKey.has(c.attrKey)) byKey.set(c.attrKey, c);
  const rows = plan.rows.map((r) => settle(r, byKey.get(r.attrKey)));
  const anySaved = rows.some((r) => r.value !== 'not-saved');
  // A value that did not save has proof 'blocked', which is never done, so "all done" needs no separate all-saved test.
  const proofsDone = rows.every((r) => r.proof === 'confirmed' || r.proof === 'not-requested');
  const overall: Overall = !anySaved ? 'nothing-saved' : proofsDone ? 'all-done' : 'partly-done';
  return { overall, rows, retryConfirmOnly: rows.filter((r) => r.proof === 'failed').map((r) => r.attrKey) };
}
